import React, { useContext, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { updateSharedVoicemail } from 'flow/actions/pbx-actions';
import Loading from 'flow/components/ui-components/Loading';
import AppContext from 'flow/contexts/app-context';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import Page from 'flow/components/Setting/Page';
import { routes } from 'flow/types/routes';
import BaseInput from 'flow/components/ui-components/BaseInput';

import { VoicemailKey } from 'flow/types/EntityKeys';

const SharedVoicemailEditor = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { entityKey, setting } = useParams<{
        entityKey: VoicemailKey;
        setting: string;
    }>();

    const voicemailDTO = useAppSelector((state) =>
        state.pbx.getIn(['voicemails', entityKey]),
    );

    const { i18next } = useContext(AppContext);

    const handleSave = useCallback(
        (text: string) => {
            if (!voicemailDTO) return;

            const voicemailDTOWithoutVoiceMessages =
                voicemailDTO.delete('voiceMessages');

            const updateFriendlyVoicemailDTO = (() => {
                switch (setting) {
                    case 'name':
                        return voicemailDTOWithoutVoiceMessages.set(
                            'description',
                            text,
                        );
                    case 'number':
                        return voicemailDTOWithoutVoiceMessages.setIn(
                            ['settings', 'smsNotificationNumber', 'number'],
                            () => text,
                        );
                    case 'email':
                        return voicemailDTOWithoutVoiceMessages.setIn(
                            ['settings', 'email'],
                            () => text,
                        );
                    default:
                        throw new Error('Unsupported setting: ' + setting);
                }
            })();

            dispatch(
                updateSharedVoicemail(entityKey, updateFriendlyVoicemailDTO),
            );
        },
        [dispatch, voicemailDTO, entityKey, setting],
    );

    if (!voicemailDTO) {
        return <Loading />;
    }

    const title = (() => {
        switch (setting) {
            case 'name':
                return i18next.t('pbx.name');
            case 'number':
                return i18next.t('pbx.number');
            case 'email':
                return i18next.t('pbx.email');
            default:
                return null;
        }
    })();

    const value = (() => {
        switch (setting) {
            case 'name':
                return voicemailDTO?.get('description');
            case 'number':
                return voicemailDTO?.getIn(['settings', 'mobileNumber']);
            case 'email':
                return voicemailDTO?.getIn(['settings', 'email']);
            default:
                return null;
        }
    })();

    return (
        <Page
            header={title}
            onClickBack={() =>
                history.push(
                    generatePath(routes['pbx.voicemails.entityKey.settings'], {
                        entityKey,
                    }),
                )
            }
        >
            {value !== null ? (
                <BaseInput
                    onChange={(e) => handleSave(e.target.value)}
                    defaultValue={value}
                />
            ) : null}
        </Page>
    );
};

export default SharedVoicemailEditor;
